// =====================================================================
// DashboardAdmin.jsx — Day 9 Hybrid dashboard for admin/coordinator/finance/sav
// =====================================================================
// Matches the approved mockup (Hybrid "Polished Ops").
// Aggregated data from GET /api/dashboard/admin (single call).
// =====================================================================

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { dashboard as dashboardApi, medicalCerts as medCertApi } from '../services/api.js';
import { useAuth } from '../hooks/useAuth.jsx';

function formatHHMMfromHours(h) {
  if (h === null || h === undefined || !Number.isFinite(h) || h === 0) return '0:00';
  const sign = h > 0 ? '+' : '-';
  const totalMin = Math.round(Math.abs(h) * 60);
  const H = Math.floor(totalMin / 60);
  const M = totalMin % 60;
  return `${sign}${H}:${String(M).padStart(2, '0')}`;
}
function fmtDate(ymd) {
  if (!ymd) return '—';
  const [y, m, d] = ymd.split('-');
  return `${d}/${m}/${y}`;
}
function fmtRel(iso) {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 60) return `il y a ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `il y a ${h}h`;
  const d = Math.floor(h / 24);
  return `il y a ${d}j`;
}

const LEAVE_TYPE_LABELS = {
  VA: 'Vacances annuelles', VAN: 'VA N+1', VAEN1: 'VA reportées',
  AA: 'Absence autorisée', REC: 'Récupération', MAT: 'Maternité', PAT: 'Paternité',
  MAL: 'Maladie', 'VA-0.5': 'Demi-journée',
};

export default function DashboardAdmin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('week'); // unused server-side for now
  const [drillDown, setDrillDown] = useState(null); // { title, items, render }

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const d = await dashboardApi.getAdmin();
      setData(d);
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // Auto-refresh every 90s
    const t = setInterval(load, 90 * 1000);
    return () => clearInterval(t);
  }, []);

  if (loading && !data) {
    return <div style={{ padding: 32, color: '#64748b' }}>Chargement du tableau de bord...</div>;
  }
  if (error) {
    return (
      <div style={{ padding: 24, background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, color: '#991b1b', margin: 20 }}>
        <strong>Erreur :</strong> {error}
        <button onClick={load} style={{ marginLeft: 12 }}>Réessayer</button>
      </div>
    );
  }
  if (!data) return null;

  const today = new Date();
  const fmtFR = today.toLocaleDateString('fr-BE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const totalAttention = (data.kpi.tsPending || 0) + (data.kpi.leavesPending || 0) + (data.sidebar.enAttente.overtimeDecisionsPending || 0);

  return (
    <div style={{ padding: 24, maxWidth: 1500, margin: '0 auto' }}>
      {/* Greeting */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Bonjour {user?.name?.split(' ')[0] || 'Admin'} 👋</h1>
          <div style={{ color: '#64748b', fontSize: 13, marginTop: 4 }}>{fmtFR} · {totalAttention} action(s) en attente</div>
        </div>
        <div style={{ display: 'inline-flex', gap: 2, background: '#f1f5f9', padding: 3, borderRadius: 8 }}>
          {['today','week','month'].map((f) => (
            <button key={f} onClick={() => setFilter(f)} style={pillBtn(filter === f)}>
              {f === 'today' ? "Aujourd'hui" : f === 'week' ? 'Cette semaine' : 'Ce mois'}
            </button>
          ))}
        </div>
      </div>

      {/* Alert strip */}
      {data.alerts && data.alerts.length > 0 && (
        <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
          {data.alerts.map((a, i) => {
            const bg = a.severity === 'CRIT' ? '#fef2f2' : a.severity === 'WARN' ? '#fffbeb' : '#eff6ff';
            const border = a.severity === 'CRIT' ? '#dc2626' : a.severity === 'WARN' ? '#f59e0b' : '#3b82f6';
            const ico = a.severity === 'CRIT' ? '🚨' : a.severity === 'WARN' ? '⚠' : 'ℹ';
            return (
              <div key={i} style={{ flex: 1, minWidth: 240, padding: '12px 14px', background: bg, borderLeft: `4px solid ${border}`, borderRadius: 10, display: 'flex', gap: 10, alignItems: 'center', fontSize: 12 }}>
                <span style={{ fontSize: 18 }}>{ico}</span>
                <div style={{ flex: 1 }}>
                  <strong style={{ display: 'block', fontSize: 13, color: '#0f172a', marginBottom: 1 }}>{a.title}</strong>
                  <span style={{ color: '#64748b', fontSize: 11 }}>{a.detail}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* KPI Hero Row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 14, marginBottom: 22 }}>
        <KpiCard
          label="👥 Présents aujourd'hui"
          value={`${data.kpi.presentsToday}`}
          sub={` / ${data.kpi.totalUsers}`}
          onClick={() => navigate('/temps#presence')}
        />
        <KpiCard
          label="✓ TS à valider"
          value={data.kpi.tsPending}
          badge={data.kpi.tsPending > 0 ? data.kpi.tsPending : null}
          onClick={() => navigate('/temps#validation')}
        />
        <KpiCard
          label="🌴 Congés à valider"
          value={data.kpi.leavesPending}
          badge={data.kpi.leavesPending > 0 ? data.kpi.leavesPending : null}
          onClick={() => navigate('/conges#valider')}
        />
        <KpiCard
          label="🔥 Heures sup. équipe"
          value={formatHHMMfromHours(data.kpi.overtimeMonthCumul)}
          sub=" ce mois"
          onClick={() => navigate('/temps#overtime:soldes-operateurs')}
        />
        <KpiCard
          label="💰 Revenue trajets (mois)"
          value={`${Math.round(data.kpi.monthToInvoice || 0)} €`}
          onClick={() => navigate('/trajets#facturation')}
        />
      </div>

      {/* Main 2-col layout */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 18, marginBottom: 22 }}>

        {/* LEFT: Modules grid */}
        <div>
          <div style={sectionTitleStyle}>MODULES OPÉRATIONNELS</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 14 }}>

            <ModuleCard
              icon="⏱️" iconBg="#dbeafe" iconColor="#1e40af"
              title="Temps & Présence"
              subtitle={`${data.modules.temps.weekHours}h cette semaine`}
              badge={data.modules.temps.tsPending > 0 ? `${data.modules.temps.tsPending} à valider` : null}
              rows={[
                ['Heures encodées (sem.)', `${data.modules.temps.weekHours}h`],
                ['TS à valider', <Pill v={data.modules.temps.tsPending} type={data.modules.temps.tsPending > 0 ? 'warn' : 'neutral'} />],
                ['Sans encodage aujourd\'hui', <Pill v={data.modules.temps.absentNoJustif} type={data.modules.temps.absentNoJustif > 0 ? 'alert' : 'ok'} />],
                ['Période verrouillée', data.modules.temps.periodLocked ? `${data.modules.temps.periodLocked} 🔒` : '—'],
              ]}
              footer={[
                ['Validation TS', '/temps#validation'],
                ['Présence', '/temps#presence'],
              ]}
            />

            <ModuleCard
              icon="🌴" iconBg="#d1fae5" iconColor="#065f46"
              title="Congés"
              subtitle={`${data.modules.conges.pending} en attente · ${data.modules.conges.approvedThisWeek} approuvés`}
              badge={data.modules.conges.pending > 0 ? `${data.modules.conges.pending} à valider` : null}
              rows={[
                ['En attente', <Pill v={data.modules.conges.pending} type={data.modules.conges.pending > 0 ? 'warn' : 'neutral'} />],
                ['Off cette semaine', `${data.modules.conges.activeThisWeek} personnes`],
                ['Soldes VA < 3j', <Pill v={data.modules.conges.lowBalances} type={data.modules.conges.lowBalances > 0 ? 'alert' : 'ok'} />],
              ]}
              footer={[
                ['À valider', '/conges#valider'],
                ['Soldes', '/conges#soldes-users'],
              ]}
            />

            <ModuleCard
              icon="🔥" iconBg="#fef3c7" iconColor="#78350f"
              title="Heures supplémentaires"
              subtitle={`${formatHHMMfromHours(data.modules.overtime.monthCumul)} cumul équipe`}
              badge={data.modules.overtime.overCap > 0 ? `${data.modules.overtime.overCap} sur plafond` : null}
              rows={[
                ['Cumul équipe (mois)', formatHHMMfromHours(data.modules.overtime.monthCumul)],
                ['≥ 35h (alerte)', <Pill v={data.modules.overtime.approaching} type={data.modules.overtime.approaching > 0 ? 'warn' : 'neutral'} />],
                ['≥ 40h (plafond)', <Pill v={data.modules.overtime.overCap} type={data.modules.overtime.overCap > 0 ? 'alert' : 'ok'} />],
                ['Décisions en attente', data.modules.overtime.decisionsPending],
              ]}
              extra={data.modules.overtime.topUsers?.length > 0 && (
                <div style={{ marginTop: 8, fontSize: 11, color: '#64748b' }}>
                  Top : {data.modules.overtime.topUsers.map((u, i) => (
                    <span key={i}><strong>{u.name} {formatHHMMfromHours(u.hours)}</strong>{i < data.modules.overtime.topUsers.length - 1 ? ', ' : ''}</span>
                  ))}
                </div>
              )}
              footer={[
                ['Soldes opérateurs', '/temps#overtime:soldes-operateurs'],
                ['Décisions', '/temps#overtime:soldes'],
              ]}
            />

            <ModuleCard
              icon="🚚" iconBg="#fed7aa" iconColor="#7c2d12"
              title="Trajets"
              subtitle={`${data.modules.trips.thisWeek} cette semaine · ${Math.round(data.modules.trips.monthRevenue || 0)} €`}
              badge={data.modules.trips.unbilled > 0 ? `${data.modules.trips.unbilled} non facturés` : null}
              rows={[
                ['Aujourd\'hui', data.modules.trips.today],
                ['Cette semaine', data.modules.trips.thisWeek],
                ['Non facturés > 7j', <Pill v={data.modules.trips.unbilled} type={data.modules.trips.unbilled > 0 ? 'warn' : 'ok'} />],
                ['Revenue mois', `${Math.round(data.modules.trips.monthRevenue || 0)} €`],
              ]}
              footer={[['Liste', '/trajets#trajets'], ['Facturation', '/trajets#facturation']]}
            />

            <ModuleCard
              icon="📦" iconBg="#ede9fe" iconColor="#5b21b6"
              title="Prélèvements"
              subtitle={`${data.modules.supply.thisWeek} cette semaine`}
              rows={[
                ['Aujourd\'hui', data.modules.supply.today],
                ['Cette semaine', data.modules.supply.thisWeek],
                ['Ce mois', data.modules.supply.monthCount],
              ]}
              footer={[['Liste', '/prelevements']]}
            />

            <ModuleCard
              icon="🖨️" iconBg="#fce7f3" iconColor="#831843"
              title="Impressions"
              subtitle={`${data.modules.printing.thisWeek} cette semaine`}
              rows={[
                ['Aujourd\'hui', data.modules.printing.today],
                ['Cette semaine', data.modules.printing.thisWeek],
                ['Ce mois', data.modules.printing.monthCount],
              ]}
              footer={[['Liste', '/impressions']]}
            />

            <ModuleCard
              icon="📊" iconBg="#ccfbf1" iconColor="#134e4a"
              title="Inventaire"
              subtitle={`${data.modules.inventory.itemsActive} items actifs`}
              badge={data.modules.inventory.countsOpen > 0 ? `${data.modules.inventory.countsOpen} comptage(s) ouvert(s)` : null}
              rows={[
                ['Items actifs', data.modules.inventory.itemsActive],
                ['Comptages ouverts', data.modules.inventory.countsOpen],
              ]}
              footer={[['Stock', '/inventaire'], ['Comptages', '/inventaire']]}
            />

            <ModuleCard
              icon="🟦" iconBg="#cffafe" iconColor="#155e75"
              title="Palettes"
              subtitle={`${data.modules.pallets.thisWeek} mouvements sem.`}
              rows={[
                ['Aujourd\'hui', data.modules.pallets.today],
                ['Cette semaine', data.modules.pallets.thisWeek],
                ['Ce mois', data.modules.pallets.monthCount],
              ]}
              footer={[['Mouvements', '/palettes']]}
            />
          </div>
        </div>

        {/* RIGHT: Sticky sidebar */}
        <div style={{ position: 'sticky', top: 16, alignSelf: 'start', display: 'flex', flexDirection: 'column', gap: 14 }}>

          <MedCertWidget />

          <SidebarCard title="⏰ EN ATTENTE">
            <SbRow label="TS à valider" value={data.sidebar.enAttente.tsPending} color={data.sidebar.enAttente.tsPending > 0 ? '#f59e0b' : '#0f172a'}
              onClick={() => data.sidebar.enAttente.tsPending > 0 && setDrillDown({
                title: `✓ TS à valider (${data.sidebar.enAttente.tsPending})`,
                kind: 'ts',
                items: data.sidebar.enAttente.details?.ts || [],
              })}
            />
            <SbRow label="Congés à valider" value={data.sidebar.enAttente.leavesPending} color={data.sidebar.enAttente.leavesPending > 0 ? '#f59e0b' : '#0f172a'}
              onClick={() => data.sidebar.enAttente.leavesPending > 0 && setDrillDown({
                title: `🌴 Congés à valider (${data.sidebar.enAttente.leavesPending})`,
                kind: 'leaves',
                items: data.sidebar.enAttente.details?.leaves || [],
              })}
            />
            <SbRow label="Décisions h. sup." value={data.sidebar.enAttente.overtimeDecisionsPending}
              onClick={() => data.sidebar.enAttente.overtimeDecisionsPending > 0 && setDrillDown({
                title: `🔥 Décisions h. sup. (${data.sidebar.enAttente.overtimeDecisionsPending})`,
                kind: 'otDec',
                items: data.sidebar.enAttente.details?.otDecisions || [],
              })}
            />
            <SbRow label="Total" value={data.sidebar.enAttente.total} color="#4f46e5" bold />
          </SidebarCard>

          <SidebarCard title="👥 PRÉSENCE LIVE">
            <SbRow label="Présents" value={data.sidebar.presenceLive.present} color="#16a34a"
              onClick={() => data.sidebar.presenceLive.present > 0 && setDrillDown({
                title: `👥 Présents aujourd'hui (${data.sidebar.presenceLive.present})`,
                kind: 'presence',
                items: data.sidebar.presenceLive.details?.present || [],
              })}
            />
            <SbRow label="En congé" value={data.sidebar.presenceLive.conge} color="#f59e0b"
              onClick={() => data.sidebar.presenceLive.conge > 0 && setDrillDown({
                title: `🌴 En congé aujourd'hui (${data.sidebar.presenceLive.conge})`,
                kind: 'presence',
                items: data.sidebar.presenceLive.details?.conge || [],
              })}
            />
            <SbRow label="Maladie" value={data.sidebar.presenceLive.mal} color="#dc2626"
              onClick={() => data.sidebar.presenceLive.mal > 0 && setDrillDown({
                title: `🤒 Maladie aujourd'hui (${data.sidebar.presenceLive.mal})`,
                kind: 'presence',
                items: data.sidebar.presenceLive.details?.mal || [],
              })}
            />
            <SbRow label="Non encodé" value={data.sidebar.presenceLive.noEntry} color="#94a3b8"
              onClick={() => data.sidebar.presenceLive.noEntry > 0 && setDrillDown({
                title: `❓ Non encodé aujourd'hui (${data.sidebar.presenceLive.noEntry})`,
                kind: 'presence',
                items: data.sidebar.presenceLive.details?.noEntry || [],
              })}
            />
          </SidebarCard>

          <SidebarCard title="📅 ABSENTS — 14 JOURS">
            {data.sidebar.offThisWeek.length === 0 ? (
              <div style={{ padding: 8, color: '#94a3b8', fontSize: 12 }}>Aucun congé en cours ou à venir</div>
            ) : data.sidebar.offThisWeek.map((l) => (
              <div key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid #f1f5f9', fontSize: 12 }}>
                <span style={{ width: 24, height: 24, borderRadius: '50%', background: '#6366f1', color: 'white', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, flexShrink: 0 }}>
                  {(l.user.name || '?').split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase()}
                </span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 500 }}>{l.user.name}</div>
                  <div style={{ fontSize: 10, color: '#94a3b8' }}>{l.type} {fmtDate(l.startDate)}→{fmtDate(l.endDate)}</div>
                </div>
                <span style={{ padding: '2px 6px', borderRadius: 4, fontSize: 9, fontWeight: 700, background: '#dcfce7', color: '#166534' }}>✓</span>
              </div>
            ))}
          </SidebarCard>

          <SidebarCard title="⚙ RACCOURCIS">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <ShortcutLink to="/temps#mes-heures" label="+ Encoder TS" />
              <ShortcutLink to="/conges#direct" label="+ Nouveau congé direct" />
              <ShortcutLink to="/temps#overtime:ajouter" label="+ Encoder heures sup." />
              <ShortcutLink to="/trajets#trajets" label="+ Nouveau trajet" />
              <ShortcutLink to="/admin/period-locks" label="🔒 Verrouiller période" />
            </div>
          </SidebarCard>
        </div>
      </div>

      {/* Coverage warnings heatmap */}
      <CoverageHeatmap onNavigate={(path) => navigate(path)} />

      {/* Drill-down modal */}
      {drillDown && (
        <DrillDownModal
          title={drillDown.title}
          kind={drillDown.kind}
          items={drillDown.items}
          onClose={() => setDrillDown(null)}
          onNavigate={(path) => { setDrillDown(null); navigate(path); }}
        />
      )}

      {/* Activity feed */}
      <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 12, padding: 18 }}>
        <h3 style={{ margin: '0 0 14px', fontSize: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
          🔔 Activité récente — à valider/réviser
          <span style={{ background: '#fef3c7', color: '#78350f', fontSize: 11, padding: '2px 8px', borderRadius: 10, fontWeight: 700 }}>
            {(data.recentActivity.pendingLeaves?.length || 0) + (data.recentActivity.submittedTS?.length || 0)}
          </span>
        </h3>
        {(data.recentActivity.pendingLeaves || []).map((a) => (
          <FeedItem
            key={'lv_' + a.leaveId}
            icon="🌴" iconBg="#dcfce7"
            actor={a.userName}
            text={` a demandé un congé ${a.leaveType} du ${fmtDate(a.startDate)} au ${fmtDate(a.endDate)}`}
            meta={fmtRel(a.submittedAt)}
            actions={[{ label: '✓ Approuver', primary: true, onClick: () => navigate('/conges#valider') }]}
          />
        ))}
        {(data.recentActivity.submittedTS || []).map((a) => (
          <FeedItem
            key={'ts_' + a.tsEntryId}
            icon="⏱️" iconBg="#dbeafe"
            actor={a.userName || '?'}
            text={` a soumis un TS sur ${a.projectName} (${Math.round(a.durationMin / 60 * 100) / 100}h) le ${fmtDate(a.date)}`}
            meta={fmtRel(a.submittedAt)}
            actions={[{ label: 'Voir', primary: true, onClick: () => navigate('/temps#validation') }]}
          />
        ))}
        {(data.recentActivity.pendingLeaves?.length || 0) + (data.recentActivity.submittedTS?.length || 0) === 0 && (
          <div style={{ padding: 24, color: '#94a3b8', fontSize: 13, textAlign: 'center' }}>Aucune activité récente.</div>
        )}
      </div>
    </div>
  );
}

// =====================================================================
// Sub-components
// =====================================================================

function KpiCard({ label, value, sub, badge, onClick }) {
  return (
    <div onClick={onClick} style={{
      background: 'white', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16,
      position: 'relative', overflow: 'hidden', cursor: onClick ? 'pointer' : 'default', transition: 'all 0.15s',
    }}
      onMouseEnter={(e) => onClick && (e.currentTarget.style.transform = 'translateY(-2px)') && (e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)')}
      onMouseLeave={(e) => onClick && (e.currentTarget.style.transform = 'translateY(0)') && (e.currentTarget.style.boxShadow = 'none')}
    >
      {badge && (
        <span style={{ position: 'absolute', top: 12, right: 12, background: '#dc2626', color: 'white', fontSize: 10, padding: '2px 7px', borderRadius: 10, fontWeight: 700 }}>{badge}</span>
      )}
      <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600, marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 700, color: '#0f172a', lineHeight: 1 }}>
        {value}
        {sub && <span style={{ fontSize: 14, color: '#94a3b8', fontWeight: 400 }}>{sub}</span>}
      </div>
    </div>
  );
}

function ModuleCard({ icon, iconBg, iconColor, title, subtitle, badge, rows, extra, footer }) {
  return (
    <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, transition: 'box-shadow 0.15s' }}
      onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.06)'}
      onMouseLeave={(e) => e.currentTarget.style.boxShadow = 'none'}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <span style={{ width: 36, height: 36, borderRadius: 8, background: iconBg, color: iconColor, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 17, flexShrink: 0 }}>{icon}</span>
        <div>
          <div style={{ fontWeight: 600, fontSize: 14 }}>{title}</div>
          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>{subtitle}</div>
        </div>
        {badge && <span style={{ marginLeft: 'auto', background: '#fef3c7', color: '#78350f', fontSize: 10, padding: '3px 8px', borderRadius: 10, fontWeight: 700 }}>{badge}</span>}
      </div>
      <div>
        {rows.map(([l, v], i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0' }}>
            <span style={{ color: '#64748b', fontSize: 12 }}>{l}</span>
            <span style={{ fontWeight: 600, color: '#0f172a', fontSize: 13 }}>{v}</span>
          </div>
        ))}
      </div>
      {extra}
      {footer && footer.length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 10, paddingTop: 10, borderTop: '1px solid #f1f5f9' }}>
          {footer.map(([label, to], i) => (
            <Link key={i} to={to} style={{ color: '#4f46e5', textDecoration: 'none', fontSize: 12, fontWeight: 500 }}>→ {label}</Link>
          ))}
        </div>
      )}
    </div>
  );
}

function Pill({ v, type }) {
  const styles = {
    warn:    { bg: '#fef3c7', fg: '#78350f' },
    alert:   { bg: '#fee2e2', fg: '#991b1b' },
    ok:      { bg: '#dcfce7', fg: '#166534' },
    neutral: { bg: '#f1f5f9', fg: '#475569' },
  }[type || 'neutral'];
  return <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, background: styles.bg, color: styles.fg }}>{v}</span>;
}

function SidebarCard({ title, children }) {
  return (
    <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16 }}>
      <h3 style={{ margin: '0 0 12px', fontSize: 12, fontWeight: 700, color: '#0f172a', textTransform: 'uppercase', letterSpacing: 0.5 }}>{title}</h3>
      {children}
    </div>
  );
}

// Day 10 — Widget self-contained: certificats médicaux en attente.
// Se auto-ascunde dacă userul nu are permisiunea (fetch → 403 → render null).
function MedCertWidget() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);  // null = loading/hidden, [] = empty
  useEffect(() => {
    medCertApi.list({ status: 'EN_ATTENTE' })
      .then((list) => setItems(list))
      .catch(() => setItems(null));  // 403 (no perm) sau eroare → ascuns
  }, []);
  if (!items || items.length === 0) return null;
  return (
    <div style={{ background: 'linear-gradient(135deg,#fff7ed,#fff)', border: '1px solid #fed7aa', borderRadius: 12, padding: 16 }}>
      <div style={{ fontSize: 11, color: '#9a3412', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>🩺 Justificatifs médicaux</div>
      <div style={{ fontSize: 28, fontWeight: 800, color: '#c2410c', lineHeight: 1.1, marginTop: 2 }}>{items.length}</div>
      <div style={{ fontSize: 11, color: '#9a3412', marginBottom: 6 }}>en attente / manquants</div>
      {items.slice(0, 4).map((c) => (
        <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, padding: '4px 0', borderTop: '1px solid #fed7aa', color: '#7c2d12' }}>
          <span>{c.user?.name || c.userId}</span>
          <span>{c.workdaysCount}j</span>
        </div>
      ))}
      <div style={{ textAlign: 'right', marginTop: 8 }}>
        <span onClick={() => navigate('/certificats-medicaux')} style={{ color: '#c2410c', fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>Voir tout →</span>
      </div>
    </div>
  );
}

function SbRow({ label, value, color, bold, onClick }) {
  const clickable = !!onClick && value > 0;
  return (
    <div
      onClick={clickable ? onClick : undefined}
      style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '6px 4px', borderBottom: '1px solid #f1f5f9',
        cursor: clickable ? 'pointer' : 'default',
        borderRadius: 4, transition: 'background 0.1s',
      }}
      onMouseEnter={(e) => { if (clickable) e.currentTarget.style.background = '#f1f5f9'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
      title={clickable ? 'Cliquer pour voir le détail' : ''}
    >
      <span style={{ color: '#64748b', fontSize: 12 }}>{label}</span>
      <span style={{ fontWeight: bold ? 800 : 700, color: color || '#0f172a', fontSize: 14, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        {value}
        {clickable && <span style={{ fontSize: 9, color: '#94a3b8' }}>→</span>}
      </span>
    </div>
  );
}

// ----- Drill-down modal -----
function DrillDownModal({ title, kind, items, onClose, onNavigate }) {
  function fmtMin(min) {
    if (!min) return '0h00';
    const h = Math.floor(min / 60);
    const m = min % 60;
    return `${h}h${String(m).padStart(2, '0')}`;
  }
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: 'white', borderRadius: 10, maxWidth: 720, width: '100%', maxHeight: '85vh',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        <div style={{ padding: '14px 18px', background: '#4f46e5', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, fontSize: 15, color: 'white' }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.2)', color: 'white', border: 0, width: 26, height: 26, borderRadius: 4, cursor: 'pointer', fontSize: 14 }}>×</button>
        </div>
        <div style={{ padding: 14, overflowY: 'auto', flex: 1 }}>
          {items.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>Aucun élément</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #cbd5e1' }}>
                  {kind === 'ts' && <>
                    <th style={thStyle}>Opérateur</th><th style={thStyle}>Date</th><th style={thStyle}>Projet</th><th style={thStyle}>Tâche</th><th style={thStyle}>Durée</th>
                  </>}
                  {kind === 'leaves' && <>
                    <th style={thStyle}>Opérateur</th><th style={thStyle}>Type</th><th style={thStyle}>Du</th><th style={thStyle}>Au</th><th style={thStyle}>Motif</th>
                  </>}
                  {kind === 'otDec' && <>
                    <th style={thStyle}>Opérateur</th><th style={thStyle}>Mois</th><th style={thStyle}>Solde</th>
                  </>}
                  {kind === 'presence' && <>
                    <th style={thStyle}>Opérateur</th><th style={thStyle}>Username</th><th style={thStyle}>Rôle</th><th style={thStyle}>Types</th><th style={thStyle}>Total</th>
                  </>}
                </tr>
              </thead>
              <tbody>
                {items.map((it, i) => (
                  <tr key={it.id || it.userId || i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    {kind === 'ts' && <>
                      <td style={tdStyle}>{it.userName}</td>
                      <td style={tdStyle}>{it.date}</td>
                      <td style={tdStyle}>{it.projectName}</td>
                      <td style={tdStyle} title={it.taskName}>{(it.taskName || '').slice(0, 30)}</td>
                      <td style={tdStyle}>{fmtMin(it.durationMin)}</td>
                    </>}
                    {kind === 'leaves' && <>
                      <td style={tdStyle}>{it.userName}</td>
                      <td style={tdStyle}><strong>{it.type}</strong></td>
                      <td style={tdStyle}>{it.startDate}</td>
                      <td style={tdStyle}>{it.endDate}</td>
                      <td style={tdStyle} title={it.reason}>{(it.reason || '').slice(0, 30) || '—'}</td>
                    </>}
                    {kind === 'otDec' && <>
                      <td style={tdStyle}>{it.userName}</td>
                      <td style={tdStyle}>{it.month}</td>
                      <td style={tdStyle}>{it.balance > 0 ? '+' : ''}{it.balance}h</td>
                    </>}
                    {kind === 'presence' && <>
                      <td style={tdStyle}>{it.userName}</td>
                      <td style={tdStyle}>{it.username}</td>
                      <td style={tdStyle}>{it.role}</td>
                      <td style={tdStyle}>{(it.types || []).join(', ') || '—'}</td>
                      <td style={tdStyle}>{it.totalMin != null ? fmtMin(it.totalMin) : '—'}</td>
                    </>}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div style={{ padding: '10px 18px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc' }}>
          <span style={{ fontSize: 11, color: '#64748b' }}>{items.length} élément(s)</span>
          <div style={{ display: 'flex', gap: 8 }}>
            {kind === 'ts' && <button onClick={() => onNavigate('/temps#validation')} style={navBtnStyle}>→ Aller à Validation TS</button>}
            {kind === 'leaves' && <button onClick={() => onNavigate('/conges#valider')} style={navBtnStyle}>→ Aller à Congés À valider</button>}
            {kind === 'otDec' && <button onClick={() => onNavigate('/temps#overtime:soldes')} style={navBtnStyle}>→ Aller à Décisions h. sup.</button>}
            {kind === 'presence' && <button onClick={() => onNavigate('/temps#presence')} style={navBtnStyle}>→ Aller à Présence</button>}
            <button onClick={onClose} style={{ padding: '6px 14px', background: 'white', border: '1px solid #cbd5e1', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>Fermer</button>
          </div>
        </div>
      </div>
    </div>
  );
}
const thStyle = { padding: '8px 10px', textAlign: 'left', fontSize: 10, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.3 };
const tdStyle = { padding: '7px 10px', color: '#0f172a' };
const navBtnStyle = { padding: '6px 12px', background: '#4f46e5', color: 'white', border: 0, borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 500 };

// =====================================================================
// CoverageHeatmap — panel of N days with absent intensity
// =====================================================================
const DOW_NAMES = ['DIM', 'LUN', 'MAR', 'MER', 'JEU', 'VEN', 'SAM'];
const LEAVE_PILL_STYLES = {
  VA:    { bg: '#dbeafe', fg: '#1e40af', icon: '🌴' },
  'VA-0.5': { bg: '#e0f2fe', fg: '#075985', icon: '🌴' },
  VAN:   { bg: '#dbeafe', fg: '#1e40af', icon: '🌴' },
  VAEN1: { bg: '#dbeafe', fg: '#1e40af', icon: '🌴' },
  AA:    { bg: '#fef3c7', fg: '#92400e', icon: '⚠' },
  REC:   { bg: '#f3e8ff', fg: '#6b21a8', icon: '🔥' },
  MAL:   { bg: '#fee2e2', fg: '#991b1b', icon: '🤒' },
  MAT:   { bg: '#fdf4ff', fg: '#86198f', icon: '👶' },
  PAT:   { bg: '#ecfeff', fg: '#0e7490', icon: '👶' },
};
// Day 11++ (01-06-2026) — couleur par NOMBRE ABSOLU d'absents (demande Silviu):
//   0 -> neutre | 1-2 -> vert | 3 -> orange | >=4 -> rouge (escalade >=7 rouge foncé)
function bucketStyle(severity, absentCount) {
  if (severity === 'weekend')  return { bg: '#f1f5f9', fg: '#cbd5e1' };
  if (severity === 'holiday')  return { bg: 'repeating-linear-gradient(45deg, #fce7f3, #fce7f3 3px, white 3px, white 6px)', fg: '#9f1239' };
  if (severity === 'crit')     return { bg: absentCount >= 7 ? '#fca5a5' : '#fecaca', fg: absentCount >= 7 ? '#7f1d1d' : '#991b1b' };  // >=4 rouge
  if (severity === 'warn')     return { bg: '#fed7aa', fg: '#7c2d12' };  // 3 orange
  // ok
  if (absentCount === 0) return { bg: '#f1f5f9', fg: '#64748b' };       // 0 neutre
  return { bg: '#dcfce7', fg: '#166534' };                              // 1-2 vert
}

function CoverageHeatmap({ onNavigate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(14);
  const [groupId, setGroupId] = useState('');
  const [groups, setGroups] = useState([]);
  const [openDay, setOpenDay] = useState(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const params = { days };
      if (groupId) params.groupId = groupId;
      const d = await import('../services/api.js').then((m) => m.dashboard.getCoverage(params));
      setData(d);
    } catch (e) {
      setError(e?.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days, groupId]);

  // Load groups for filter
  useEffect(() => {
    import('../services/api.js').then((m) => m.groups.list()).then((g) => setGroups(g || []));
  }, []);

  if (loading && !data) return null;
  if (error || !data) return null;

  return (
    <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 12, padding: 18, marginBottom: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>📅 Couverture équipe — {days} jours à venir</h3>
          <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>
            {data.summary.critical > 0 && <span style={{ color: '#dc2626', fontWeight: 600 }}>{data.summary.critical} critique{data.summary.critical > 1 ? 's' : ''}</span>}
            {data.summary.critical > 0 && data.summary.warning > 0 && ' · '}
            {data.summary.warning > 0 && <span style={{ color: '#f59e0b', fontWeight: 600 }}>{data.summary.warning} attention</span>}
            {data.summary.critical === 0 && data.summary.warning === 0 && <span style={{ color: '#16a34a' }}>✓ Aucune alerte sur cette période</span>}
            {' · '}équipe : {data.totalTeam} pers.
          </div>
        </div>
        <div style={{ display: 'inline-flex', gap: 8 }}>
          <select value={groupId} onChange={(e) => setGroupId(e.target.value)} style={{ padding: '5px 8px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 12 }}>
            <option value="">Toute l'équipe</option>
            {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
          <select value={days} onChange={(e) => setDays(parseInt(e.target.value, 10))} style={{ padding: '5px 8px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 12 }}>
            <option value={14}>14 jours</option>
            <option value={30}>30 jours</option>
            <option value={60}>60 jours</option>
          </select>
        </div>
      </div>

      {/* Day 9: heatmap responsive — 14 cols always, wraps to multiple rows for 30/60 days */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(14, minmax(0, 1fr))', gap: 4 }}>
        {data.dayList.map((d) => {
          const s = bucketStyle(d.severity, d.absentCount);
          const clickable = d.absentCount > 0 && !d.isWeekend && !d.isHoliday;
          // Smaller font/padding when more days shown
          const compact = days > 14;
          return (
            <div
              key={d.date}
              onClick={() => clickable && setOpenDay(d)}
              style={{
                aspectRatio: '1 / 1', borderRadius: compact ? 6 : 8, padding: compact ? 2 : 4,
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                background: s.bg, color: s.fg, cursor: clickable ? 'pointer' : 'default',
                transition: 'transform 0.1s',
                outline: d.date === data.today ? '2px solid #4f46e5' : 'none',
                outlineOffset: -2,
                minHeight: 0,
              }}
              onMouseEnter={(e) => { if (clickable) { e.currentTarget.style.transform = 'scale(1.1)'; e.currentTarget.style.zIndex = '2'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)'; } }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.zIndex = '1'; e.currentTarget.style.boxShadow = 'none'; }}
              title={d.isWeekend ? `Weekend ${d.date}` : d.isHoliday ? `JF ${d.date}: ${d.holidayName}` : `${d.date} · ${d.absentCount} absent(s) — ${d.coveragePct}% disponible`}
            >
              <div style={{ fontSize: compact ? 7 : 9, opacity: 0.7, fontWeight: 600 }}>{DOW_NAMES[d.dow]}</div>
              <div style={{ fontWeight: 700, fontSize: compact ? 10 : 13 }}>{d.date.slice(8, 10)}</div>
              {d.isWeekend ? null : d.isHoliday ? (
                <div style={{ fontSize: compact ? 7 : 8, marginTop: 1 }}>JF</div>
              ) : (
                <div style={{ fontSize: compact ? 7 : 9, opacity: 0.85, fontWeight: d.severity === 'crit' ? 700 : 400, display: 'flex', alignItems: 'center', gap: 2 }}>
                  <span>{d.absentApproved}{d.severity === 'crit' ? ' ⚠' : ''}</span>
                  {(d.absentPending || 0) > 0 && (
                    <span style={{ fontSize: compact ? 6 : 8, padding: '0 3px', background: 'repeating-linear-gradient(45deg, #fef3c7, #fef3c7 2px, transparent 2px, transparent 4px)', color: '#78350f', border: '1px solid #f59e0b', borderRadius: 3, fontWeight: 600 }}
                      title={`${d.absentPending} en attente`}>
                      +{d.absentPending}
                    </span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div style={{ marginTop: 8, fontSize: 11, color: '#94a3b8', textAlign: 'center' }}>
        Affiche {data.dayList.length} jours · cliquer sur un jour pour le détail
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 12, fontSize: 11, color: '#64748b', alignItems: 'center' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><span style={{ width: 12, height: 12, background: '#f1f5f9', borderRadius: 3, display: 'inline-block' }}></span> 0 absent</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><span style={{ width: 12, height: 12, background: '#dcfce7', borderRadius: 3, display: 'inline-block' }}></span> 1-2</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><span style={{ width: 12, height: 12, background: '#fed7aa', borderRadius: 3, display: 'inline-block' }}></span> 3 (attention)</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><span style={{ width: 12, height: 12, background: '#fecaca', borderRadius: 3, display: 'inline-block' }}></span> ≥4 (critique)</span>
        <span style={{ marginLeft: 'auto', color: '#dc2626', fontWeight: 600 }}>⚠ Seuil critique : ≥4 absents</span>
      </div>

      {openDay && (
        <CoverageDayModal day={openDay} total={data.totalTeam} onClose={() => setOpenDay(null)} onNavigate={onNavigate} />
      )}
    </div>
  );
}

function CoverageDayModal({ day, total, onClose, onNavigate }) {
  const sev = day.severity;
  const headerBg = sev === 'crit' ? '#dc2626' : sev === 'warn' ? '#f59e0b' : '#16a34a';
  const headerIcon = sev === 'crit' ? '🚨' : sev === 'warn' ? '⚠' : '✓';
  const dateLabel = new Date(day.date + 'T12:00:00Z').toLocaleDateString('fr-BE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: 10, maxWidth: 600, width: '100%', maxHeight: '85vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '14px 18px', background: headerBg, color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 15, color: 'white' }}>{headerIcon} {dateLabel}</h3>
            <div style={{ fontSize: 11, opacity: 0.9, marginTop: 2 }}>
              {day.coveragePct}% disponible · {day.absentApproved ?? day.absentCount} confirmé(s)
              {(day.absentPending || 0) > 0 && <> · <span style={{ color: '#fef3c7' }}>+{day.absentPending} en attente</span></>}
              {' '}sur {total}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.2)', color: 'white', border: 0, width: 26, height: 26, borderRadius: 4, cursor: 'pointer', fontSize: 14 }}>×</button>
        </div>
        <div style={{ padding: 16, overflowY: 'auto', flex: 1 }}>
          {day.absentDetails.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>Personne en congé</div>
          ) : (() => {
            const approved = day.absentDetails.filter((p) => p.status === 'APPROVED');
            const pending  = day.absentDetails.filter((p) => p.status === 'PENDING');
            const renderItem = (p, isPending) => {
              const pillStyle = LEAVE_PILL_STYLES[p.type] || { bg: '#f1f5f9', fg: '#475569', icon: '·' };
              return (
                <div key={`${p.userId}-${p.status}`} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px',
                  background: isPending ? 'repeating-linear-gradient(45deg, #fffbeb, #fffbeb 6px, #fef9c3 6px, #fef9c3 12px)' : '#f8fafc',
                  borderRadius: 8, border: isPending ? '1px solid #f59e0b' : '1px solid #e2e8f0',
                }}>
                  <span style={{ fontSize: 16 }}>{pillStyle.icon}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 500, fontSize: 13 }}>{p.userName}</div>
                    <div style={{ fontSize: 11, color: '#94a3b8' }}>{p.username} · {p.startDate} → {p.endDate}</div>
                  </div>
                  {isPending && <span style={{ fontSize: 10, padding: '2px 7px', background: '#fef3c7', color: '#78350f', borderRadius: 10, fontWeight: 700 }}>EN ATTENTE</span>}
                  <span style={{ padding: '3px 10px', borderRadius: 10, fontSize: 11, fontWeight: 600, background: pillStyle.bg, color: pillStyle.fg }}>{p.type}</span>
                </div>
              );
            };
            return (
              <>
                {approved.length > 0 && (
                  <>
                    <h4 style={{ margin: '0 0 8px', fontSize: 12, color: '#166534', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      ✓ Confirmé ({approved.length})
                    </h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
                      {approved.map((p) => renderItem(p, false))}
                    </div>
                  </>
                )}
                {pending.length > 0 && (
                  <>
                    <h4 style={{ margin: '0 0 8px', fontSize: 12, color: '#92400e', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      ⏳ En attente de validation ({pending.length})
                    </h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {pending.map((p) => renderItem(p, true))}
                    </div>
                  </>
                )}
              </>
            );
          })()}
        </div>
        <div style={{ padding: '10px 18px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: 8, background: '#f8fafc' }}>
          <button onClick={() => { onClose(); onNavigate('/conges#timeline'); }} style={navBtnStyle}>📅 Voir Vue d'ensemble</button>
          <button onClick={onClose} style={{ padding: '6px 14px', background: 'white', border: '1px solid #cbd5e1', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>Fermer</button>
        </div>
      </div>
    </div>
  );
}

function ShortcutLink({ to, label }) {
  return (
    <Link to={to} style={{ padding: '8px 10px', background: 'linear-gradient(90deg, #f8fafc, white)', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12, color: '#0f172a', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 8 }}>
      {label} <span style={{ marginLeft: 'auto', opacity: 0.4 }}>→</span>
    </Link>
  );
}

function FeedItem({ icon, iconBg, actor, text, meta, actions }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
      <span style={{ width: 28, height: 28, borderRadius: '50%', background: iconBg, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, flexShrink: 0 }}>{icon}</span>
      <div style={{ flex: 1, fontSize: 13 }}>
        <span style={{ fontWeight: 600 }}>{actor}</span>{text}
        <div style={{ color: '#94a3b8', fontSize: 11, marginTop: 2 }}>{meta}</div>
      </div>
      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
        {actions?.map((a, i) => (
          <button key={i} onClick={a.onClick} style={{ padding: '4px 10px', border: a.primary ? 0 : '1px solid #cbd5e1', background: a.primary ? '#4f46e5' : 'white', color: a.primary ? 'white' : '#0f172a', borderRadius: 6, fontSize: 11, cursor: 'pointer' }}>{a.label}</button>
        ))}
      </div>
    </div>
  );
}

// Styles
const sectionTitleStyle = { fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700, margin: '0 0 12px' };
function pillBtn(active) {
  return {
    padding: '6px 14px', border: 0, background: active ? 'white' : 'transparent', borderRadius: 6, cursor: 'pointer',
    fontSize: 12, color: active ? '#0f172a' : '#64748b', fontWeight: 500,
    boxShadow: active ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
  };
}
